import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "../hooks/useToast";
import {
  PlusIcon,
  ChevronRightIcon,
} from '@radix-ui/react-icons';
import { Button } from '../components/common/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/common/Card';
import Layout from '../components/common/Layout';
import { startTimer, stopTimer } from '../api/activity-log';
import QuickAddActivity from '../components/activity/QuickAddActivity';
import ActivityCard from '../components/activity/ActivityCard';
import { getActivitySummary } from '../api/activity';
import { initializeFirebaseMessaging } from '../utils/firebase';

const HomePage = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Home | Zilean";
  }, []);

  useEffect(() => {
    fetchActivities();
    
    // Initialize Firebase messaging if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      initializeFirebaseMessaging().catch(error => {
        console.error('Failed to initialize Firebase messaging:', error);
      });
    }
  }, []);

  const fetchActivities = async () => {
    try {
      const data = await getActivitySummary();
      // Sort activities: in-progress first, then by last_updated_date
      const sortedActivities = data.sort((a, b) => {
        // First, sort by in_progress status
        if (a.in_progress && !b.in_progress) return -1;
        if (!a.in_progress && b.in_progress) return 1;
        
        // Then sort by last_updated_date within each group
        const dateA = new Date(a.last_updated_date || 0);
        const dateB = new Date(b.last_updated_date || 0);
        return dateB - dateA;
      });
      setActivities(sortedActivities);
      setLoading(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to fetch activities',
        type: "error"
      });
      setLoading(false);
    }
  };

  const handleTimerToggle = async (activityId, inProgress) => {
    // Optimistically update the UI first
    const updatedActivities = activities.map(a => 
      a.id === activityId ? { ...a, in_progress: !inProgress, last_updated_date: new Date().toISOString() } : a
    ).sort((a, b) => {
      if (a.in_progress && !b.in_progress) return -1;
      if (!a.in_progress && b.in_progress) return 1;
      const dateA = new Date(a.last_updated_date || 0);
      const dateB = new Date(b.last_updated_date || 0);
      return dateB - dateA;
    });
    setActivities(updatedActivities);

    try {
      if (inProgress) {
        await stopTimer(activityId);
        toast({
          title: "Success",
          description: "Timer stopped",
          type: "success"
        });
      } else {
        await startTimer(activityId);
        toast({
          title: "Success",
          description: "Timer started",
          type: "success"
        });
      }
    } catch (error) {
      // Revert the optimistic update on error
      const revertedActivities = activities.map(a => 
        a.id === activityId ? { ...a, in_progress: inProgress, last_updated_date: new Date().toISOString() } : a
      ).sort((a, b) => {
        if (a.in_progress && !b.in_progress) return -1;
        if (!a.in_progress && b.in_progress) return 1;
        const dateA = new Date(a.last_updated_date || 0);
        const dateB = new Date(b.last_updated_date || 0);
        return dateB - dateA;
      });
      setActivities(revertedActivities);
      
      toast({
        title: "Error",
        description: error.message || 'Failed to toggle timer',
        type: "error"
      });
    }
  };

  const handleActivityAdded = (newActivityId, name) => {
    const newActivity = {
      id: newActivityId,
      name: name,
      in_progress: true,
      total_duration_in_minutes: 0,
      today_duration: 0
    };
    setActivities([newActivity, ...activities]);
    setShowAddForm(false);
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">
              It starts when you take the first step
            </p>
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className={showAddForm ? "button-glow-active" : "button-glow"}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            New Activity
          </Button>
        </div>

        {/* Quick Add Form */}
        <AnimatePresence>
          {showAddForm && (
            <QuickAddActivity
              onClose={() => setShowAddForm(false)}
              onActivityAdded={handleActivityAdded}
            />
          )}
        </AnimatePresence>

        {/* Stats Overview */}
        <div className="hidden md:grid gap-4 md:grid-cols-3">
          <Card 
            className="bg-green-600/10 hover:bg-green-500/20 dark:bg-green-500/5 dark:hover:bg-green-500/10 transition-colors overflow-hidden relative"
          >
            {/* Modern Habits Illustration */}
            <div className="absolute inset-0 pointer-events-none">
              <svg
                viewBox="0 35 130 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute right-0 top-0 h-full opacity-10"
              >
                {/* Circular Progress Track */}
                <circle 
                  cx="100" 
                  cy="100" 
                  r="50" 
                  stroke="currentColor" 
                  strokeWidth="8" 
                  className="text-[#184918] dark:text-white" 
                  strokeDasharray="4 4"
                />
                
                {/* Check Mark */}
                <path
                  d="M70 100l20 20 40-40"
                  stroke="currentColor"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#184918] dark:text-white"
                >
                </path>
              </svg>
            </div>
            <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
              <CardTitle className="text-sm font-medium text-[#184918] dark:text-white">
                Analyze
              </CardTitle>
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold text-[#184918] dark:text-white">See Patterns Clearly</div>
              <p className="text-xs text-muted-foreground pr-[100px]">
              Visualize your activities to understand habits, optimize time, make better decisions
              </p>
            </CardContent>
          </Card>
          <Card 
            className="bg-purple-600/10 hover:bg-purple-500/20 dark:bg-purple-500/5 dark:hover:bg-purple-500/10 transition-colors overflow-hidden relative"
          >
            {/* Modern Goal Setting Illustration */}
            <div className="absolute inset-0 pointer-events-none">
              <svg
                viewBox="0 11 115 120"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute right-0 top-0 h-full opacity-10"
              >
                <circle cx="100" cy="100" r="80" stroke="currentColor" strokeWidth="4" className="text-[#4c1d95] dark:text-white" />
                <circle cx="100" cy="100" r="60" stroke="currentColor" strokeWidth="4" className="text-[#4c1d95] dark:text-white" strokeDasharray="4 4" />
                <path d="M100 20V180M20 100H180" stroke="currentColor" strokeWidth="2" className="text-[#4c1d95] dark:text-white" strokeDasharray="8 8" />
                <circle cx="100" cy="100" r="10" fill="currentColor" className="text-[#4c1d95] dark:text-white" />
                <path d="M160 100A60 60 0 0 0 100 40" stroke="currentColor" strokeWidth="8" className="text-[#4c1d95] dark:text-white" strokeLinecap="round">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 100 100"
                    to="360 100 100"
                    dur="20s"
                    repeatCount="indefinite"
                  />
                </path>
                <g className="text-[#4c1d95] dark:text-white">
                  <circle cx="160" cy="100" r="5" fill="currentColor">
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 100 100"
                      to="360 100 100"
                      dur="20s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </g>
              </svg>
            </div>
            <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
              <CardTitle className="text-sm font-medium text-[#4c1d95] dark:text-white">
                Activities
              </CardTitle>
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold text-[#4c1d95] dark:text-white">Track every activity</div>
              <p className="text-xs text-muted-foreground pr-[100px]">
              Daily tracking helps clear the mental clutter, letting you focus on what truly matters
              </p>
            </CardContent>
          </Card>
          <Card 
            className="bg-blue-600/10 hover:bg-blue-500/20 dark:bg-blue-500/5 dark:hover:bg-blue-500/10 transition-colors overflow-hidden relative"
          >
            {/* Modern Calendar Illustration */}
            <div className="absolute inset-0 pointer-events-none">
              <svg
                viewBox="0 30 130 120"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute right-0 top-0 h-full opacity-10"
              >
                {/* Calendar Frame */}
                <rect x="40" y="40" width="120" height="120" rx="8" stroke="currentColor" strokeWidth="4" className="text-[#184984] dark:text-white" />
                {/* Calendar Header */}
                <rect x="40" y="40" width="120" height="30" rx="8" fill="currentColor" className="text-[#184984] dark:text-white" />
                {/* Calendar Grid Lines */}
                <path d="M40 85H160M40 115H160M40 145H160M80 70V160M120 70V160" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      className="text-[#184984] dark:text-white" 
                      strokeDasharray="4 4" />
                {/* Highlight Circle */}
                <circle cx="100" cy="100" r="12" fill="currentColor" className="text-[#184984] dark:text-white" opacity="0.5">
                  <animate
                    attributeName="r"
                    values="12;14;12"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
                {/* Animated Dots */}
                <circle cx="60" cy="130" r="4" fill="currentColor" className="text-[#184984] dark:text-white">
                  <animate
                    attributeName="opacity"
                    values="1;0.5;1"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle cx="140" cy="100" r="4" fill="currentColor" className="text-[#184984] dark:text-white">
                  <animate
                    attributeName="opacity"
                    values="0.5;1;0.5"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </div>
            <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
              <CardTitle className="text-sm font-medium text-[#184984] dark:text-white">
                Calendar
              </CardTitle>
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold text-[#184984] dark:text-white">View Schedule</div>
              <p className="text-xs text-muted-foreground pr-[100px]">
              Get a clear timeline of your activities—see your progress unfold day by day, week by week
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Activities Grid */}
        <div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">
                Your Activities
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Click on an activity to start tracking
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/activities')}
              className="text-sm whitespace-nowrap w-full sm:w-auto"
            >
              View All
              <ChevronRightIcon className="ml-2 h-4 w-4" />
            </Button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <motion.div
                className="h-8 w-8 border-2 border-primary border-r-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            </div>
          ) : activities.length === 0 ? (
            <Card className="p-8 text-center">
              <CardContent>
                <p className="text-muted-foreground mb-4">No activities yet. Start by creating your first activity!</p>
                <Button onClick={() => setShowAddForm(true)}>
                  <PlusIcon className="mr-2 h-4 w-4" />
                  New Activity
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence mode="popLayout">
                {activities.map((activity) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    onTimerToggle={() => handleTimerToggle(activity.id, activity.in_progress)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default HomePage; 